import React from "react";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface ConfirmDiscardChangesModalProps {
    show: boolean;
    // True while a save triggered from this modal is in flight; disables the
    // actions so the drawer can't be closed out from under an in-flight save.
    isSaving: boolean;
    // When true (e.g. invalid due date or a negative total), "Save changes &
    // exit" stays disabled — otherwise the save would reach the backend and
    // fail as a generic toast.
    saveDisabled?: boolean;
    // Explains why "Save changes & exit" is disabled (e.g. the negative-total
    // reason). Only shown when the button is disabled.
    saveDisabledTooltip?: string;
    // Save the current changes; the caller closes the drawer only if the save
    // succeeds (Send/Save-before-close semantics).
    onSave: () => void;
    // Close the drawer without saving. Never deletes anything.
    onDiscard: () => void;
    // Dismiss the modal and stay in the drawer.
    onCancel: () => void;
}

export const ConfirmDiscardChangesModal: React.FC<
    ConfirmDiscardChangesModalProps
> = ({
    show,
    isSaving,
    saveDisabled = false,
    saveDisabledTooltip,
    onSave,
    onDiscard,
    onCancel,
}) => (
    <PlainModal show={show} onHide={onCancel} size="md">
        <StandardModalHeader>Unsaved changes</StandardModalHeader>
        <VStack gap={16} className="mb-4">
            <div>You have unsaved changes. What would you like to do?</div>
        </VStack>
        <HStack as="footer" gap={12} justify="end">
            <Tooltip content={saveDisabled ? saveDisabledTooltip : undefined}>
                <Button
                    type="button"
                    variant="secondary"
                    size="xl"
                    onClick={onSave}
                    disabled={isSaving || saveDisabled}
                >
                    Save changes & exit
                </Button>
            </Tooltip>
            <Button
                type="button"
                variant="destructive"
                size="xl"
                onClick={onDiscard}
                disabled={isSaving}
            >
                Discard changes
            </Button>
        </HStack>
    </PlainModal>
);
